import ComponentManager, {ComponentOptions} from "../../componentFramework";
import deepFreeze from "../../scripts/deepFreeze";

/**
 * Used to access json data that is printed out on the page using:
 * 		<script class="jsonScript" type="application/json" data-jsonScript-id="configId"> ... JSON here .. </script>
 *
 * And then use JsonScript.getInstance("configId").getData();
 */
@ComponentManager.register()
export default class JsonScript {
	public static componentStyleClass = "jsonScript";

	private static instances: Map<string, JsonScript>;
	private data: any;
	private readonly id: string;

	public static getInstance(id: string): JsonScript | undefined {
		if (JsonScript.instances === undefined) {
			return undefined;
		}
		return JsonScript.instances.get(id);
	}

	@ComponentManager.on("beforeFirstConstruction")
	public static beforeFirstConstruction() {
		JsonScript.instances = new Map<string, JsonScript>();
	}

	constructor(public readonly rootElement: HTMLElement) {
		if (!(rootElement instanceof HTMLScriptElement) || rootElement.type !== "application/json") {
			throw new Error(`Error initializing an instance of JsonScript. Element is either not a script tag or type is not set to "application/json".`);
		}

		if (rootElement.textContent === null) {
			throw new Error(`The text contents of JsonScript is null.`);
		}

		const idAttributeName = ComponentOptions.getAttributeName(this, "id");
		let id = rootElement.getAttribute(idAttributeName);
		if (id === null) {
			id = this.rootElement.id.length !== 0 ? this.rootElement.id : null;
		}
		if (id === null) {
			throw new Error(`No ID specified. Use either the "id" attribute or "${idAttributeName}" attribute. The later takes priority.`);
		}
		this.id = id;
		JsonScript.instances.set(this.id, this);
	}

	public getData(): any {
		if (this.data === undefined) {
			this.data = deepFreeze(JSON.parse(this.rootElement.textContent!));
		}
		return this.data;
	}

	@ComponentManager.on("removed")
	public componentRemoved() {
		const instance = JsonScript.instances.get(this.id);
		if (instance === this) {
			JsonScript.instances.delete(this.id);
		}
	}
}
